import { PricingTierPreview } from "@/components/PricingTierPreview";
import type {
  CloverSecurityAndTransarmorForm,
  FeeOverridesForm,
  FieldSetter,
  FleetOverridesForm,
  IcPlusPricingOverridesForm,
  VolumeDetailsForms,
} from "./types";
import { TF } from "./TF";

export function VolumeFields({
  vol,
  setVolField,
  authFeeOverride,
  feeOverrides,
  fleetOverrides,
  cloverSecurityAndTransarmor,
  icPlusPricingOverrides,
  salesCode,
  annualSecurityBundleFee,
  fieldError,
}: {
  vol: VolumeDetailsForms;
  setVolField: FieldSetter<VolumeDetailsForms>;
  // Optional -- only the send-intake auto-submit path validates these today
  // (volume/ticket must be > 0 before it skips straight to CardConnect).
  fieldError?: (field: string) => string | undefined;
  // Admin-only overrides, passed through to PricingTierPreview so the "you
  // qualify for" preview shows the EFFECTIVE fees (override if set, else
  // tier default) instead of always showing raw tier defaults — otherwise
  // an operator who discounts a fee below sees the old default up here,
  // which is exactly the confusing mismatch this is meant to avoid.
  // Omitted entirely on the merchant-facing intake form, where these
  // overrides are never exposed.
  authFeeOverride?: string;
  feeOverrides?: FeeOverridesForm;
  // Fleet (Voyager / WEX / PIN debit / EBT) and Clover Security + TransArmor
  // overrides -- same pass-through, so the full fee schedule lists every
  // non-zero fee the merchant's pricing summary will show.
  fleetOverrides?: FleetOverridesForm;
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorForm;
  // Card percentage (interchange-plus) overrides plus the sales-code pick, so
  // the fee schedule shows the same card rates as the merchant's summary.
  icPlusPricingOverrides?: IcPlusPricingOverridesForm;
  salesCode?: string;
  // Raw Annual security bundle fee override string -- same pass-through
  // rationale as authFeeOverride/feeOverrides. Omitted on the intake form.
  annualSecurityBundleFee?: string;
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <TF
          id="averageMonthlyVolume"
          label="Average monthly volume"
          value={vol.averageMonthlyVolume}
          onChange={(v) => setVolField("averageMonthlyVolume", v)}
          format="integer"
          maxLength={12}
          error={fieldError?.("averageMonthlyVolume")}
        />
        <TF
          id="highTicketAmount"
          label="Highest ticket amount"
          value={vol.highTicketAmount}
          onChange={(v) => setVolField("highTicketAmount", v)}
          format="integer"
          maxLength={12}
          error={fieldError?.("highTicketAmount")}
        />
        <TF
          id="averageTicketAmount"
          label="Average ticket amount"
          value={vol.averageTicketAmount}
          onChange={(v) => setVolField("averageTicketAmount", v)}
          format="integer"
          maxLength={12}
          error={fieldError?.("averageTicketAmount")}
        />
      </div>
      <PricingTierPreview
        monthlyVolume={vol.averageMonthlyVolume}
        authFeeOverride={authFeeOverride}
        feeOverrides={feeOverrides}
        fleetOverrides={fleetOverrides}
        cloverSecurityAndTransarmor={cloverSecurityAndTransarmor}
        icPlusPricingOverrides={icPlusPricingOverrides}
        salesCode={salesCode}
        annualSecurityBundleFee={annualSecurityBundleFee}
      />
    </>
  );
}
